import React, { useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ArrowForward as ArrowIcon,
  CheckCircle as CheckCircleIcon,
  CloudUpload as UploadIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { manufacturingAPI } from '../../services/api';
import usePageTitle from '../../hooks/usePageTitle';

const HardboardBOMUpdate = () => {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [applying, setApplying] = useState(false);
  const [applyResult, setApplyResult] = useState(null);
  const fileInputRef = useRef(null);

  usePageTitle('Hardboard BOM Update');

  const runPreview = async (selectedFile) => {
    setLoading(true);
    setPreview(null);
    setApplyResult(null);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      const response = await manufacturingAPI.hardboardBOM.preview(formData);
      setPreview(response.data);
    } catch (error) {
      const body = error.response?.data;
      if (body?.file_errors?.length) {
        setPreview(body);
      } else {
        toast.error('Preview failed: ' + (body?.error || error.message));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (event) => {
    const selected = event.target.files?.[0];
    if (!selected) return;
    const ext = selected.name.split('.').pop().toLowerCase();
    if (!['xlsx', 'xls'].includes(ext)) {
      toast.error('Please upload an Excel (.xlsx) file');
      return;
    }
    setFile(selected);
    runPreview(selected);
  };

  const handleReset = () => {
    setFile(null);
    setPreview(null);
    setApplyResult(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleApply = async () => {
    setConfirmOpen(false);
    setApplying(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await manufacturingAPI.hardboardBOM.apply(formData);
      setApplyResult(response.data);
      toast.success(
        `Updated ${response.data.swapped} BOM(s)` +
        (response.data.already_correct
          ? ` — ${response.data.already_correct} already correct`
          : '')
      );
      // Refresh so the page reflects the new state (swapped rows move to
      // "already correct").
      runPreview(file);
    } catch (error) {
      const body = error.response?.data;
      toast.error('Apply failed: ' + (body?.error || error.message));
    } finally {
      setApplying(false);
    }
  };

  const summary = preview?.summary || {};
  const canApply = Boolean(file && preview?.ready_to_apply && !loading && !applying);

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <Typography variant="h4" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
        <UploadIcon fontSize="large" />
        Hardboard BOM Update
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Upload the Sub Assembly → Hardboard spreadsheet. Column B is the correct
        hardboard for each sub-assembly: BOMs that already match are left alone, and
        the rest have their hardboard component swapped to the one in the file
        (quantities unchanged). No stock is moved.
      </Typography>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <Button variant="contained" component="label" startIcon={<UploadIcon />}>
            {file ? 'Choose Different File' : 'Upload Spreadsheet'}
            <input
              ref={fileInputRef}
              type="file"
              hidden
              accept=".xlsx,.xls"
              onChange={handleFileChange}
            />
          </Button>
          {file && <Chip label={file.name} onDelete={handleReset} />}
          <Box sx={{ flexGrow: 1 }} />
          <Tooltip
            title={
              canApply
                ? 'Swap the mismatched hardboards to the ones in the file'
                : 'Upload a file with changes to make and no errors first'
            }
          >
            <span>
              <Button
                variant="contained"
                color="success"
                disabled={!canApply}
                onClick={() => setConfirmOpen(true)}
                startIcon={applying ? <CircularProgress size={20} /> : <CheckCircleIcon />}
              >
                {applying ? 'Applying…' : `Update ${summary.will_change || 0} BOM(s)`}
              </Button>
            </span>
          </Tooltip>
        </Box>
      </Paper>

      {applyResult && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setApplyResult(null)}>
          <Typography variant="subtitle2">
            Updated {applyResult.swapped} BOM(s)
            {applyResult.already_correct > 0 &&
              ` — ${applyResult.already_correct} already correct`}
          </Typography>
        </Alert>
      )}

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {preview && !loading && (
        <>
          {preview.file_errors?.length > 0 && (
            <Alert severity="error" sx={{ mb: 2 }} icon={<WarningIcon />}>
              {preview.file_errors.map((e, idx) => (
                <Typography key={idx} variant="body2">{e}</Typography>
              ))}
            </Alert>
          )}

          {preview.summary && (
            <Paper sx={{ p: 3, mb: 3 }}>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                <Chip label={`${summary.total_rows} rows`} />
                <Chip
                  label={`${summary.will_change} will change`}
                  color="success"
                  icon={<CheckCircleIcon />}
                />
                <Chip label={`${summary.already_correct} already correct`} color="info" />
                {summary.errors > 0 && (
                  <Chip label={`${summary.errors} errors`} color="error" icon={<WarningIcon />} />
                )}
                {summary.duplicates > 0 && (
                  <Chip label={`${summary.duplicates} duplicates`} color="warning" />
                )}
                {summary.skipped_blank > 0 && (
                  <Chip label={`${summary.skipped_blank} incomplete rows skipped`} />
                )}
                <Box sx={{ flexGrow: 1 }} />
                {preview.ready_to_apply ? (
                  <Chip label="Ready to apply" color="success" />
                ) : (
                  <Chip
                    label={summary.errors > 0 ? 'Not ready — see errors' : 'Nothing to change'}
                    color={summary.errors > 0 ? 'error' : 'default'}
                  />
                )}
              </Box>
            </Paper>
          )}

          {preview.errors?.length > 0 && (
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 2, color: 'error.main' }}>
                Errors ({summary.errors})
              </Typography>
              <TableContainer sx={{ maxHeight: 300 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Row</TableCell>
                      <TableCell>Sheet</TableCell>
                      <TableCell>Sub Assembly</TableCell>
                      <TableCell>Hardboard</TableCell>
                      <TableCell>Problem</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {preview.errors.map((e, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{e.row_number}</TableCell>
                        <TableCell>{e.sheet || '-'}</TableCell>
                        <TableCell>{e.sub_assembly}</TableCell>
                        <TableCell>{e.hardboard}</TableCell>
                        <TableCell>{e.error}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}

          {preview.changes?.length > 0 && (
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Will Change ({summary.will_change})
              </Typography>
              <TableContainer sx={{ maxHeight: 500 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Sub Assembly</TableCell>
                      <TableCell>Current Hardboard</TableCell>
                      <TableCell />
                      <TableCell>New Hardboard</TableCell>
                      <TableCell align="right">Qty</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {preview.changes.map((c, idx) => (
                      <TableRow key={idx}>
                        <TableCell>
                          <Typography variant="body2" fontWeight="bold">{c.sub_assembly}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {c.sub_assembly_name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="error.main">
                            {c.current_hardboard}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {c.current_hardboard_name}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ px: 0 }}>
                          <ArrowIcon fontSize="small" color="action" />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="bold" color="success.main">
                            {c.new_hardboard}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {c.new_hardboard_name}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">{c.quantity}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}

          {preview.already_correct?.length > 0 && (
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 2, color: 'info.main' }}>
                Already Correct ({summary.already_correct})
              </Typography>
              <TableContainer sx={{ maxHeight: 300 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Sub Assembly</TableCell>
                      <TableCell>Hardboard</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {preview.already_correct.map((c, idx) => (
                      <TableRow key={idx}>
                        <TableCell>
                          <Typography variant="body2">{c.sub_assembly}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{c.hardboard}</Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                            {c.hardboard_name}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}

          {preview.duplicates?.length > 0 && (
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 1, color: 'warning.main' }}>
                Duplicate Rows ({summary.duplicates})
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                These rows repeat an earlier sub-assembly + hardboard pair; the first
                occurrence is used.
              </Typography>
              {preview.duplicates.map((d, idx) => (
                <Typography key={idx} variant="body2">
                  Row {d.row_number}{d.sheet ? ` (${d.sheet})` : ''}: {d.sub_assembly} → {d.hardboard}
                </Typography>
              ))}
            </Paper>
          )}

          <Divider sx={{ my: 2 }} />
          <Typography variant="body2" color="text.secondary">
            Applying only re-points BOM lines at the correct hardboard — quantities,
            operations and stock levels are untouched. Re-uploading the same file is
            safe: fixed rows just show as &quot;already correct&quot;.
          </Typography>
        </>
      )}

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Update Hardboard BOMs?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will swap the hardboard component on{' '}
            <strong>{summary.will_change}</strong> BOM(s) to the hardboard listed in
            the file, keeping quantities unchanged.{' '}
            {summary.already_correct > 0 &&
              `${summary.already_correct} BOM(s) already match and will be left alone. `}
            Continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleApply} variant="contained" color="success">
            Update BOMs
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default HardboardBOMUpdate;
